import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Form from "@saleor/components/Form";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { maybe } from "@saleor/misc";
import { OrderDetails_order } from "@saleor/motherOrders/types/OrderDetails";
import { OrderCourierEnum } from "@saleor/motherOrders/utils/enums";
import { DialogProps } from "@saleor/types";
import {
  DeliveryTypeEnum as DelEnum,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import classNames from "classnames";
import React from "react";

import {
  hasNoError,
  initialErrorValidation,
  useShippingLabelValidation
} from "./formValidation";
import { SubmitButtonValidation } from "./handlers";

const useStyles = makeStyles(
  {
    buttonConfirm: {
      backgroundColor: "#FFD15C !important",
      marginLeft: "8px"
    },
    buttonDisabled: {
      backgroundColor: "#D9D9D9 !important",
      marginLeft: "8px"
    },
    dialogActions: {
      paddingBottom: "0px",
      paddingLeft: "0px",
      paddingRight: "0px"
    },
    dialogTextMarginBottom: {
      marginBottom: "24px"
    },
    labelMargin: {
      marginBottom: "3px"
    },
    muiDialogContentOverrides: {
      "& .MuiInputLabel-root": {
        // "& .MuiFormLabel-asterisk": {
        //   color: "#db3131"
        // },
        overflowX: "visible"
      },
      paddingBottom: "10px"
    },
    textFieldMarginBottom: {
      marginBottom: "11px"
    }
  },
  {
    name: "OrderMarkAsShippedCustomStyles"
  }
);

export interface OrderGenerateShippingLabelProps extends DialogProps {
  open: boolean;
  onUpdateShippingLabel: (
    courier: string,
    awbNo: string,
    dispatchNo: string,
    specialNotes: string,
    orderStatus?: SmopOrderStatusEnum,
    hasChanged?: boolean
  ) => void;
  type: string;
  orderData: OrderDetails_order;
}

export interface GenerateShippingLabelFormData {
  awbNumber: string;
  courier: string;
  dispatchNumber: string;
  hasChanged: boolean;
  specialNotes: string;
}

const OrderGenerateShippingLabel: React.SFC<OrderGenerateShippingLabelProps> = props => {
  const { onClose, open, onUpdateShippingLabel, type, orderData } = props;
  const [errorValidation, setErrorValdiation] = React.useState(
    initialErrorValidation
  );

  const classes = useStyles(props);

  const initialForm: GenerateShippingLabelFormData = {
    awbNumber: maybe(() => orderData.awbNo, "") ? orderData?.awbNo : "",
    courier: maybe(() => orderData.courier, "") ? orderData?.courier : "",
    dispatchNumber: maybe(() => orderData.dispatchNo, "")
      ? orderData?.dispatchNo
      : "",
    hasChanged: false,
    specialNotes: maybe(() => orderData.specialNotes, "")
      ? orderData?.specialNotes
      : ""
  };

  const handleSubmit = (formData: GenerateShippingLabelFormData) => {
    switch (type) {
      case "ORDER_LIST_ACTION":
        onUpdateShippingLabel(
          formData.courier,
          formData.awbNumber,
          formData.dispatchNumber,
          formData.specialNotes,
          orderData?.smopStatus,
          formData.hasChanged
        );
        onClose();
        break;
      case "ORDER_VIEW_ACTION":
        const validate = useShippingLabelValidation(formData);
        if (
          maybe(() => orderData.shippingMethod.deliveryType) ===
            DelEnum.EXPRESS ||
          maybe(() => orderData.shippingMethod.deliveryType) ===
            DelEnum.EXPRESS_LONG_DISTANCE
        ) {
          onUpdateShippingLabel(
            formData.courier,
            formData.awbNumber,
            formData.dispatchNumber,
            formData.specialNotes
          );
          onClose();
        } else if (
          (maybe(
            () => orderData.shippingMethod.deliveryType === DelEnum.NEXT_DAY
          ) ||
            maybe(
              () =>
                orderData.shippingMethod.deliveryType ===
                DelEnum.NATIONWIDE_DELIVERY
            )) &&
          hasNoError(validate)
        ) {
          setErrorValdiation(validate);
          onUpdateShippingLabel(
            formData.courier,
            formData.awbNumber,
            formData.dispatchNumber,
            formData.specialNotes
          );
          onClose();
        } else {
          setErrorValdiation(validate);
        }
        break;
      default:
        break;
    }
  };

  const handleResetValidation = () => {
    setErrorValdiation(initialErrorValidation);
  };

  const couriers = [
    { label: "IN HOUSE", value: OrderCourierEnum.IN_HOUSE },
    { label: "GRAB EXPRESS MANUAL", value: OrderCourierEnum.GRAB },
    { label: "LALAMOVE", value: OrderCourierEnum.LALAMOVE },
    { label: "GRAB EXPRESS AUTO", value: OrderCourierEnum.GRAB_EXPRESS },
    { label: "OTHERS", value: OrderCourierEnum.OTHERS }
  ];

  return (
    <Dialog
      onClose={() => {
        onClose();
        handleResetValidation();
      }}
      open={open}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Generate Shipping Label</DialogTitle>
      <DialogContent className={classes.muiDialogContentOverrides}>
        {/* <DialogContentText className={classes.dialogTextMarginBottom}>
        <FormattedMessage defaultMessage="Are you sure you want to mark this order as Shipped?" />
      </DialogContentText> */}
        <Form initial={initialForm} onSubmit={handleSubmit}>
          {({ data: formData, change, hasChanged, submitWithOpts }) => (
            <>
              <InputLabel htmlFor="courier" className={classes.labelMargin}>
                Courier
              </InputLabel>
              <SingleSelectField
                className={classes.textFieldMarginBottom}
                disabled={orderData?.courier ? true : false}
                value={formData.courier}
                choices={couriers}
                onChange={change}
                placeholder="courier"
                name="courier"
              />

              <InputLabel
                htmlFor="awbNumber"
                // required={
                //   maybe(() => orderData.shippingMethod.deliveryType) ===
                //   DelEnum.NEXT_DAY
                //     ? true
                //     : false
                // }
                className={classes.labelMargin}
              >
                AWB No.
              </InputLabel>
              <TextField
                id="awbNumberTxt"
                autoFocus
                fullWidth
                placeholder="Input AWB No. here"
                name="awbNumber"
                onChange={change}
                value={formData.awbNumber}
                inputProps={{
                  type: "text"
                }}
                className={classes.textFieldMarginBottom}
                error={errorValidation.awbNumber}
                helperText={errorValidation.awbNumber}
                autoComplete="off"
                disabled={
                  orderData?.smopStatus === SmopOrderStatusEnum.SHIPPED ||
                  orderData?.smopStatus === SmopOrderStatusEnum.COMPLETED ||
                  orderData?.smopStatus === SmopOrderStatusEnum.CANCELED
                }
              />
              <InputLabel
                htmlFor="dispatchNumber"
                // required={
                //   maybe(() => orderData.shippingMethod.deliveryType) ===
                //   DelEnum.NEXT_DAY
                //     ? true
                //     : false
                // }
                className={classes.labelMargin}
              >
                Dispatch No.
              </InputLabel>
              <TextField
                id="dispatchNumTxt"
                fullWidth
                placeholder="Input Dispatch No. here"
                name="dispatchNumber"
                onChange={change}
                value={formData.dispatchNumber}
                inputProps={{
                  type: "text"
                }}
                className={classes.textFieldMarginBottom}
                error={errorValidation.dispatchNumber}
                helperText={errorValidation.dispatchNumber}
                autoComplete="off"
                disabled={
                  orderData?.smopStatus === SmopOrderStatusEnum.SHIPPED ||
                  orderData?.smopStatus === SmopOrderStatusEnum.COMPLETED ||
                  orderData?.smopStatus === SmopOrderStatusEnum.CANCELED
                }
              />
              <InputLabel
                htmlFor="specialNotes"
                className={classes.labelMargin}
              >
                Special Notes
              </InputLabel>
              <TextField
                id="specialNotesTxt"
                fullWidth
                placeholder="Input special notes here"
                name="specialNotes"
                onChange={change}
                value={formData.specialNotes}
                inputProps={{
                  type: "text"
                }}
                multiline={true}
                disabled={
                  orderData?.smopStatus === SmopOrderStatusEnum.SHIPPED ||
                  orderData?.smopStatus === SmopOrderStatusEnum.COMPLETED ||
                  orderData?.smopStatus === SmopOrderStatusEnum.CANCELED
                }
              />
              <DialogActions className={classes.dialogActions}>
                <Button
                  id="cancelBtn"
                  onClick={() => {
                    onClose();
                    handleResetValidation();
                  }}
                  color="default"
                  variant="outlined"
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  id="confirmBtn"
                  onClick={() => {
                    submitWithOpts({ hasChanged });
                  }}
                  disabled={SubmitButtonValidation(type, formData)}
                  color="primary"
                  variant="contained"
                  className={classNames(classes.buttonConfirm, {
                    [classes.buttonDisabled]: SubmitButtonValidation(
                      type,
                      formData
                    )
                  })}
                  fullWidth
                >
                  {type === "ORDER_VIEW_ACTION" ? "Confirm" : "Print"}
                </Button>
              </DialogActions>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default OrderGenerateShippingLabel;
